<script setup lang="ts">
import { reactive, ref } from "vue";
import { DuplicatePhoneResponseStatus, useAccount } from "#/account/useAccount";
import { useModal } from "#/form/useModal";
import FormInput from "#/form/FormInput.vue";
import AddPhoneStepCode from "#/account/phone/AddPhoneStepCode.vue";
import { notification } from "#/utils/notification";

interface Props {
  modalId?: string;
}

const props = defineProps<Props>();

const { state, phone } = useAccount();
const { close } = useModal();

const phoneValue = ref("");
const errorMessage = ref("");

const steps = reactive({
  isFirstStep: true,
  isHasDuplicate: false,
  isCodeStep: false,
});

const checkDuplicate = async () => {
  try {
    const response = await phone.checkDuplicate(phoneValue.value);

    if (response === DuplicatePhoneResponseStatus.Duplicate) {
      changeStep("isHasDuplicate");
    }

    if (response === DuplicatePhoneResponseStatus.Success) {
      changeStep("isCodeStep");
    }
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const closeModal = () => {
  clearSteps();
  steps.isFirstStep = true;
  phoneValue.value = "";

  if (props.modalId) close(props.modalId);
};

const clearSteps = () => {
  let key: keyof typeof steps;
  for (key in steps) {
    steps[key] = false;
  }
};

const changeStep = (step: keyof typeof steps) => {
  clearSteps();
  errorMessage.value = "";
  steps[step] = true;
};

const changePhone = async () => {
  await phone.clearSession();
  changeStep("isFirstStep");
};

const approve = async (code: string) => {
  try {
    await phone.verificationCode(code);
    closeModal();
    notification.success("Номер телефона успешно изменен");
    window.location.reload();
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const resendCode = async () => {
  try {
    errorMessage.value = "";
    await phone.resendCode();
  } catch (e: any) {
    errorMessage.value = e;
  }
};
</script>

<template>
  <div v-if="steps.isFirstStep">
    <span class="color-dark flex mb-20 text-small justify-center">
      Отправим SMS c кодом на указанный номер, для подтверждения
    </span>

    <form-input
      v-model="phoneValue"
      label="Новый номер телефона"
      class="mb-20"
      placeholder="+7 (900) 000-00-00"
      mask="+{7} (000) 000-00-00"
      type="tel"
      :disabled="state.isLoading"
      @keyup.enter="checkDuplicate"
    />

    <span v-if="errorMessage" class="mb-20 flex color-error">{{
      errorMessage
    }}</span>

    <button
      type="submit"
      class="w-full flex justify-center counter-form__submit btn"
      :class="state.isLoading ? 'tag-disable' : 'tag-primary'"
      :disabled="state.isLoading"
      @click="checkDuplicate"
    >
      Добавить номер к аккаунту
    </button>
  </div>

  <div v-if="steps.isHasDuplicate" class="mt-20">
    <span class="color-dark d-block mb-44 text-small text-center">
      Данный номер привязан к другому аккаунту на нашем сайте. Обратите
      внимание, что вы можете <span class="text-bold">потерять доступ</span> к
      прошлому аккаунту, если перенесете телефон. Продолжить добавление?
    </span>

    <div class="flex justify-between flex-gap-20">
      <button
        class="w-full tag-disable flex justify-center counter-form__submit btn"
        @click="closeModal"
      >
        Отменить
      </button>
      <button
        type="submit"
        class="w-full tag-primary flex justify-center counter-form__submit btn"
        @click="changeStep('isCodeStep')"
      >
        Продолжить
      </button>
    </div>
  </div>

  <div v-if="steps.isCodeStep">
    <add-phone-step-code
      :is-loading="state.isLoading"
      :phone="phoneValue"
      :error-message="errorMessage"
      @form:approve="approve"
      @form:code-resend="resendCode"
      @change-phone="changePhone"
    />
  </div>
</template>
